import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TtsStatusRow } from 'src/app/services/text-to-speech.service';
import { TtsStatusService } from '../tts-status.service';
import { describeSettings, TtsSettingRow } from './tts-settings-display';

interface StatusTheme {
  title: string;
  dot: string;
  bg: string;
  border: string;
  text: string;
}

const STATUS_THEMES: Record<string, StatusTheme> = {
  ready: { title: 'Ready', dot: '#2f9e5b', bg: '#eefaf1', border: '#c9ecd4', text: '#1f7a44' },
  pending: { title: 'Generating…', dot: '#d99a1f', bg: '#fdf6e6', border: '#f2dfa8', text: '#a97314' },
  failed: { title: 'Failed', dot: '#d64530', bg: '#fdecea', border: '#f3b9b0', text: '#a5301f' },
  none: { title: 'Not generated', dot: '#9aa1ab', bg: '#f3f4f6', border: '#dde1e7', text: '#5f6773' },
};

@Component({
  selector: 'tts-audio-panel',
  templateUrl: './tts-audio-panel.component.html',
  styleUrls: ['./tts-audio-panel.component.scss'],
  standalone: true,
  imports: [CommonModule],
})
export class TtsAudioPanelComponent {
  @Input() fieldPath: string | null = null;
  @Input() label = '';
  @Input() text = '';
  /** Blocks regenerate: unsaved text would not match the saved record. */
  @Input() formDirty = false;

  @Output() close = new EventEmitter<void>();

  settingsOpen = false;
  regenerating = false;
  regenerateError: string | null = null;

  constructor(private ttsStatus: TtsStatusService) {}

  get row(): TtsStatusRow | null {
    return this.fieldPath ? this.ttsStatus.get(this.fieldPath) : null;
  }

  // Rich-text fields arrive as HTML; show the words that get spoken, not the markup.
  get spokenText(): string {
    if (!this.text) {
      return '';
    }
    const doc = new DOMParser().parseFromString(this.text, 'text/html');
    return (doc.body.textContent ?? '').trim();
  }

  get theme(): StatusTheme | null {
    const row = this.row;
    if (row) {
      return STATUS_THEMES[row.status] ?? null;
    }
    return this.ttsStatus.loaded ? STATUS_THEMES['none'] : null;
  }

  get isNotGenerated(): boolean {
    return !this.row && this.ttsStatus.loaded;
  }

  get detail(): string {
    const row = this.row;
    if (!row) {
      return this.isNotGenerated ? 'No audio has been generated for this field yet' : '';
    }
    if (row.status === 'pending') {
      return this.ttsStatus.timedOut ? 'Still generating — check back shortly' : 'Usually takes a few seconds';
    }
    const when = new Date(row.updatedAt);
    const stamp = isNaN(when.getTime()) ? '' : when.toLocaleString();
    return row.status === 'ready' ? `Generated ${stamp}` : `Last attempt ${stamp}`;
  }

  // Only `ready`. A failed regeneration leaves the previous clip at the
  // permanent URL, and playing that would claim the field is fine.
  get hasAudio(): boolean {
    const row = this.row;
    return !!row && row.status === 'ready' && !!row.url;
  }

  get isFailed(): boolean {
    return this.row?.status === 'failed';
  }

  get isPending(): boolean {
    return this.row?.status === 'pending';
  }

  get settingRows(): TtsSettingRow[] {
    return describeSettings(this.row?.settings);
  }

  toggleSettings(): void {
    this.settingsOpen = !this.settingsOpen;
  }

  get canRegenerate(): boolean {
    return (!!this.row || this.isNotGenerated) && !!this.spokenText && !this.isPending && !this.regenerating && !this.formDirty;
  }

  regenerate(): void {
    if (!this.fieldPath || !this.canRegenerate) {
      return;
    }

    this.regenerating = true;
    this.regenerateError = null;

    // Always forced: unforced, a `ready` row with unchanged text short-circuits
    // on the fingerprint and the button silently does nothing.
    this.ttsStatus.regenerate(this.fieldPath, this.text, true).subscribe({
      next: () => {
        this.regenerating = false;
      },
      error: (err: any) => {
        this.regenerating = false;
        this.regenerateError = err?.error?.error || err?.error?.message || 'Could not start regeneration.';
      },
    });
  }
}
